import type { StripeSchoolTeacher } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import {
	Box,
	Chip,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../assets/styles/themes/tokens';

interface Props {
	teachers: StripeSchoolTeacher[];
	total: number;
	page: number;
	rowsPerPage: number;
	loading: boolean;
	onPageChange: (event: unknown, newPage: number) => void;
}

const StripeTeacherList: React.FC<Props> = ({
	teachers,
	total,
	page,
	rowsPerPage,
	loading,
	onPageChange,
}) => {
	const { t } = useTranslation();

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" padding={4}>
				<CircularProgress />
			</Box>
		);
	}

	if (teachers.length === 0) {
		return (
			<Paper elevation={0} sx={{ padding: 4, textAlign: 'center' }}>
				<Typography variant="body1" sx={{ color: palette.text.secondary }}>
					{t('No teachers found')}
				</Typography>
			</Paper>
		);
	}

	return (
		<>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>{t('Teacher').toUpperCase()}</TableCell>
							<TableCell>{t('Status').toUpperCase()}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{teachers.map((teacher) => (
							<TableRow key={teacher.id}>
								<TableCell>
									<Box display="flex" flexDirection="column">
										<Typography variant="body2" sx={{ color: palette.text.primary, fontWeight: 600 }}>
											{`${teacher.firstName} ${teacher.lastName}`.trim() || teacher.email}
										</Typography>
										<Typography variant="caption" sx={{ color: palette.text.secondary }}>
											{teacher.email}
										</Typography>
									</Box>
								</TableCell>
								<TableCell>
									<Chip
										label={teacher.status === 'invited' ? t('Invited') : t('Active')}
										color={teacher.status === 'invited' ? 'warning' : 'success'}
										size="small"
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={total}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={onPageChange}
				rowsPerPageOptions={[rowsPerPage]}
			/>
		</>
	);
};

export default StripeTeacherList;
